// src/WorkOrder.js
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import API_BASE_URL from './config/api';
import FileUpload from './Components/Shared/FileUpload';

const styles = {
  body: {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif",
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    minHeight: '100vh',
    padding: '12px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  container: {
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    padding: '20px',
    maxWidth: '800px',
    width: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '10px',
  },
  title: {
    color: '#333',
    fontSize: '28px',
    fontWeight: '600',
    margin: 0,
  },
  subtitle: {
    color: '#666',
    marginBottom: '30px',
    fontSize: '14px',
  },
  formGroup: {
    marginBottom: '20px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    color: '#333',
    fontWeight: '600',
    fontSize: '14px',
  },
  description: {
    fontSize: '12px',
    color: '#888',
    marginTop: '-5px',
    marginBottom: '8px',
    fontWeight: 'normal',
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '15px',
    fontFamily: 'inherit',
    transition: 'border-color 0.3s, box-shadow 0.3s',
    boxSizing: 'border-box',
    minHeight: '50px',
    backgroundColor: 'white',
  },
  textarea: {
    width: '100%',
    padding: '14px 16px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '15px',
    fontFamily: 'inherit',
    transition: 'border-color 0.3s, box-shadow 0.3s',
    boxSizing: 'border-box',
    resize: 'vertical',
    minHeight: '100px',
    backgroundColor: 'white',
  },
  select: {
    width: '100%',
    padding: '14px 16px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '15px',
    fontFamily: 'inherit',
    transition: 'border-color 0.3s, box-shadow 0.3s',
    boxSizing: 'border-box',
    backgroundColor: 'white',
    cursor: 'pointer',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 14px center',
    paddingRight: '40px',
    minHeight: '50px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '20px',
    marginTop: '0',
    paddingBottom: '10px',
    borderBottom: '2px solid #f0f0f0',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  sectionContainer: {
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '20px',
    backgroundColor: '#fafafa',
  },
  submitBtn: {
    width: '100%',
    padding: '14px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    marginTop: '20px',
  },
  success: {
    background: '#d4edda',
    color: '#155724',
    padding: '15px',
    borderRadius: '8px',
    marginTop: '20px',
    textAlign: 'center',
    fontWeight: '500',
  },
  error: {
    background: '#f8d7da',
    color: '#721c24',
    padding: '15px',
    borderRadius: '8px',
    marginTop: '20px',
    textAlign: 'center',
    fontWeight: '500',
  },
};

// Worker options - must match Notion select options
const WORKER_OPTIONS = [
  'Ehsan', 'Kennedy', 'Ali', 'Sean', 'Rasoul', 'Donny', 'Office',
  'Hemen', 'Alireza', 'Satyam', 'Matthew', 'Shivam', 
  'Daniel', 'Ahmed', 'Nathaniel', 'Alex', 'Muin', 'Deepak', 
  'Sabir', 'Tarun', 'Other'
];

// Worker colors for tags - distinct colors for each worker
const WORKER_COLORS = {
  'Ehsan': { bg: '#f3e5f5', border: '#9c27b0', text: '#7b1fa2' },
  'Kennedy': { bg: '#fce4ec', border: '#e91e63', text: '#c2185b' },
  'Ali': { bg: '#fff3e0', border: '#ff9800', text: '#e65100' },
  'Sean': { bg: '#e0f7fa', border: '#00bcd4', text: '#00838f' },
  'Rasoul': { bg: '#fff8e1', border: '#ffc107', text: '#f57f17' },
  'Donny': { bg: '#ffebee', border: '#f44336', text: '#c62828' },
  'Office': { bg: '#e8eaf6', border: '#3f51b5', text: '#283593' },
  'Hemen': { bg: '#fbe9e7', border: '#ff5722', text: '#d84315' },
  'Alireza': { bg: '#e1f5fe', border: '#03a9f4', text: '#0277bd' },
  'Satyam': { bg: '#f1f8e9', border: '#8bc34a', text: '#558b2f' },
  'Matthew': { bg: '#ede7f6', border: '#673ab7', text: '#512da8' },
  'Shivam': { bg: '#e0f2f1', border: '#009688', text: '#00695c' },
  'Daniel': { bg: '#e3f2fd', border: '#2196f3', text: '#1565c0' },
  'Ahmed': { bg: '#fff9c4', border: '#ffeb3b', text: '#f57f17' },
  'Nathaniel': { bg: '#f3e5f5', border: '#ab47bc', text: '#6a1b9a' },
  'Alex': { bg: '#dcedc8', border: '#689f38', text: '#33691e' },
  'Muin': { bg: '#b2dfdb', border: '#26a69a', text: '#00695c' },
  'Deepak': { bg: '#ffccbc', border: '#ff7043', text: '#bf360c' },
  'Sabir': { bg: '#d1c4e9', border: '#7e57c2', text: '#4527a0' },
  'Tarun': { bg: '#c5cae9', border: '#5c6bc0', text: '#1a237e' },
  'Other': { bg: '#f5f5f5', border: '#9e9e9e', text: '#616161' },
};

// Next Steps For Office options - must match Notion select options exactly
const NEXT_STEPS_OFFICE_OPTIONS = [
  '✅ Work Done — (Send Invoice)',
  '✅ Work Done & Extra Onsite Work Done Today — (Send Invoice for Both)',
  '✅ Work Done & Approved Follow-Up Work — (Send Invoice and Schedule Follow up Visit)',
  '✅ Work Done & Suggested Follow-Up Work — (Send Invoice and Send New Quote)',
  '🔴 Work NOT Completed — (don\'t invoice yet and schedule follow-up visit required to finish)',
  '🔴 Work NOT Completed — (No Access or missing parts)',
];

function WorkOrder() {
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('job');
  
  const [formData, setFormData] = useState({
    buildingAddress: '',
    unitNumber: '',
    startTime: '',
    endTime: '',
    descriptionOfWorkDone: '',
    partsUsedFromStock: '',
    serviceDate: new Date().toISOString().split('T')[0], // Default to today
    nextStepsForOffice: '',
    nextSteps: '',
  });
  
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [workerDropdownOpen, setWorkerDropdownOpen] = useState(false);
  const [workerSearch, setWorkerSearch] = useState('');
  const [nextStepsDropdownOpen, setNextStepsDropdownOpen] = useState(false);
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);
  const workerDropdownRef = useRef(null);
  const workerSearchInputRef = useRef(null);
  const nextStepsDropdownRef = useRef(null);
  
  // Filter workers based on search
  const filteredWorkers = WORKER_OPTIONS.filter(worker => 
    worker.toLowerCase().includes(workerSearch.toLowerCase())
  );

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (workerDropdownRef.current && !workerDropdownRef.current.contains(event.target)) {
        setWorkerDropdownOpen(false);
        setWorkerSearch(''); // Clear search when closing
      }
      if (nextStepsDropdownRef.current && !nextStepsDropdownRef.current.contains(event.target)) {
        setNextStepsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Photo URLs (uploaded to Back4App)
  const [photosBeforeUrls, setPhotosBeforeUrls] = useState([]);
  const [photosAfterUrls, setPhotosAfterUrls] = useState([]);
  const [receiptPhotoUrls, setReceiptPhotoUrls] = useState([]);
  const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [loadingJob, setLoadingJob] = useState(false);
  const [jobData, setJobData] = useState(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [lastSubmittedPayload, setLastSubmittedPayload] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [emailForCopy, setEmailForCopy] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [submittedPageId, setSubmittedPageId] = useState(null);

  // Fetch job data if jobId is present in URL
  useEffect(() => {
    const fetchJobData = async () => {
      if (!jobId) return;
      
      setLoadingJob(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/jobs/get`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ jobId }),
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch job data');
        }
        
        const data = await response.json();
        
        if (data.success && data.job) {
          const job = data.job;
          setJobData(job);
          
          // Prepopulate form with job data
          // Field names match what Notion returns
          const scheduledDate = job['Scheduled Date'];
          
          // Extract date and times from Scheduled Date object
          let serviceDate = new Date().toISOString().split('T')[0];
          let startTime = '';
          let endTime = '';
          
          if (scheduledDate) {
            if (typeof scheduledDate === 'object') {
              // Scheduled Date is an object with start and end
              if (scheduledDate.start) {
                const startDate = new Date(scheduledDate.start);
                serviceDate = startDate.toISOString().split('T')[0];
                // Extract time in HH:MM format
                startTime = startDate.toTimeString().slice(0, 5);
              }
              if (scheduledDate.end) {
                const endDate = new Date(scheduledDate.end);
                endTime = endDate.toTimeString().slice(0, 5);
              }
            } else if (typeof scheduledDate === 'string') {
              serviceDate = scheduledDate.split('T')[0];
            }
          }
          
          // Handle Worker(s) - it's an array, set all workers
          const workers = job['Worker(s)'] || job['Workers'] || [];
          const workerArray = Array.isArray(workers) ? workers : (workers ? [workers] : []);
          // Filter to only include valid worker options
          const validWorkers = workerArray.filter(w => WORKER_OPTIONS.includes(w));
          setSelectedWorkers(validWorkers);
          
          // Safely extract string value - Notion may return objects for some property types
          const extractString = (val) => {
            if (!val) return '';
            if (typeof val === 'string') return val;
            if (typeof val === 'number') return String(val);
            // Handle rollup arrays
            if (Array.isArray(val)) {
              return val.map(v => typeof v === 'string' ? v : (v?.plain_text || v?.name || JSON.stringify(v))).join(', ');
            }
            // Handle rich_text-like objects
            if (val.plain_text) return val.plain_text;
            if (val.name) return val.name;
            if (val.string) return val.string;
            // Handle rollup object
            if (val.type === 'array' && Array.isArray(val.array)) {
              return val.array.map(item => {
                if (item.type === 'rich_text') return item.rich_text?.map(t => t.plain_text).join('') || '';
                if (item.type === 'title') return item.title?.map(t => t.plain_text).join('') || '';
                return '';
              }).filter(Boolean).join(', ');
            }
            return String(val);
          };
          
          setFormData(prev => ({
            ...prev,
            buildingAddress: job['Summary'] || '',
            unitNumber: extractString(job['Unit #']),
            serviceDate: serviceDate,
            startTime: startTime,
            endTime: endTime,
          }));
          
          console.log('Job data loaded:', job);
        }
      } catch (err) {
        console.error('Error fetching job data:', err);
        // Don't show error to user - just continue with empty form
      } finally {
        setLoadingJob(false);
      }
    };

    fetchJobData();
  }, [jobId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Clear success message when user makes any changes (indicates they can submit again)
  useEffect(() => {
    if (success && lastSubmittedPayload) {
      // Build current payload to compare
      const startDateTime = formData.startTime 
        ? `${formData.serviceDate}T${formData.startTime}:00` 
        : null;
      const endDateTime = formData.endTime 
        ? `${formData.serviceDate}T${formData.endTime}:00` 
        : null;
      
      const currentPayload = {
        jobId: jobData?.notionPageId || null,
        buildingAddress: formData.buildingAddress,
        unitNumber: formData.unitNumber ? parseInt(formData.unitNumber, 10) : null,
        startTime: startDateTime,
        endTime: endDateTime,
        descriptionOfWorkDone: formData.descriptionOfWorkDone,
        partsUsedFromStock: formData.partsUsedFromStock || '',
        workers: selectedWorkers,
        serviceDate: formData.serviceDate,
        nextStepsForOffice: formData.nextStepsForOffice,
        nextSteps: formData.nextSteps || '',
        photosBeforeUrls: photosBeforeUrls,
        photosAfterUrls: photosAfterUrls,
        receiptPhotoUrls: receiptPhotoUrls,
      };
      
      // If data has changed from last submission, clear success message
      if (JSON.stringify(currentPayload) !== JSON.stringify(lastSubmittedPayload)) {
        setSuccess(false);
      }
    }
  }, [formData, selectedWorkers, photosBeforeUrls, photosAfterUrls, receiptPhotoUrls, success, lastSubmittedPayload, jobData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Format datetime fields
    const startDateTime = formData.startTime 
      ? `${formData.serviceDate}T${formData.startTime}:00` 
      : null;
    const endDateTime = formData.endTime 
      ? `${formData.serviceDate}T${formData.endTime}:00` 
      : null;

    // Photos are already uploaded to Back4App by FileUpload component
    // We just send the URLs to the backend for Notion
    const payload = {
      // Send the Notion page ID for relation (not the display ID like "NE-1626")
      jobId: jobData?.notionPageId || null,
      buildingAddress: formData.buildingAddress,
      unitNumber: formData.unitNumber ? parseInt(formData.unitNumber, 10) : null,
      startTime: startDateTime,
      endTime: endDateTime,
      descriptionOfWorkDone: formData.descriptionOfWorkDone,
      partsUsedFromStock: formData.partsUsedFromStock || '',
      workers: selectedWorkers,
      serviceDate: formData.serviceDate,
      nextStepsForOffice: formData.nextStepsForOffice,
      nextSteps: formData.nextSteps || '',
      photosBeforeUrls: photosBeforeUrls,
      photosAfterUrls: photosAfterUrls,
      receiptPhotoUrls: receiptPhotoUrls,
    };

    // Check if this is a duplicate submission (same data as last successful submit)
    if (lastSubmittedPayload && JSON.stringify(payload) === JSON.stringify(lastSubmittedPayload)) {
      alert('This work order has already been submitted. Please make changes before submitting again.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      console.log('Submitting work order:', payload);

      const response = await fetch(`${API_BASE_URL}/api/work-orders/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to submit work order');
      }

      setSuccess(true);
      // Store the submitted payload to prevent duplicate submissions
      setLastSubmittedPayload(payload);
      // Store the page ID for email functionality
      setSubmittedPageId(result.pageId);
      // Show success modal with email option
      setShowSuccessModal(true);
      setEmailSent(false);
      setEmailForCopy('');
      
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Error submitting work order:', err);
      setError(err.message || 'Failed to submit work order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show full-screen loading when fetching job data
  if (loadingJob) {
    return (
      <div style={styles.body}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: '20px',
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid rgba(255, 255, 255, 0.3)',
            borderTop: '4px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }} />
          <p style={{
            color: 'white',
            fontSize: '18px',
            fontWeight: '500',
          }}>
            Loading job details...
          </p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.body}>
      <div style={styles.container}>
        <div style={styles.header}>
          <span style={{ fontSize: '32px' }}>🔧</span>
          <h1 style={styles.title}>Submit Work Order</h1>
        </div>
        <p style={styles.subtitle}>Complete this form after finishing a service visit</p>

        {/* Job Details Section */}
        {jobId && (
          <div style={styles.sectionContainer}>
            <div style={styles.sectionTitle}>
              <span>📋</span> Job Details
            </div>

            {/* Job Info Banner */}
            <div style={{
              background: '#e8f4fd',
              border: '1px solid #b8daff',
              borderRadius: '8px',
              padding: '12px 14px',
              marginBottom: jobData && (jobData["Hudson's Notes"] || jobData['Issue Description']) ? '16px' : '0',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}>
              {jobData ? (
                <div style={{ flex: 1 }}>
                  <strong style={{ color: '#0056b3' }}>
                    {jobData['Summary'] || jobData['Job Name'] || 'Job'}
                  </strong>
                  {(jobData['ID'] || jobData['id']) && (
                    <span style={{ color: '#666', marginLeft: '8px', fontSize: '13px' }}>
                      ({jobData['ID'] || jobData['id']})
                    </span>
                  )}
                  {jobData['Client name'] && (
                    <div style={{ color: '#666', fontSize: '13px', marginTop: '4px' }}>
                      {jobData['Client name']}
                    </div>
                  )}
                  {jobData['Status'] && (
                    <span style={{ 
                      display: 'inline-block',
                      marginTop: '6px',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      background: jobData['Status'] === 'Scheduled Jobs' ? '#cce5ff' : '#d4edda',
                      color: jobData['Status'] === 'Scheduled Jobs' ? '#004085' : '#155724',
                    }}>
                      {jobData['Status']}
                    </span>
                  )}
                </div>
              ) : (
                <>
                  <span style={{ fontSize: '18px' }}>⚠️</span>
                  <span style={{ color: '#856404' }}>Job not found - fill in details manually</span>
                </>
              )}
            </div>

            {/* Office Instructions */}
            {jobData && (jobData["Hudson's Notes"] || jobData['Issue Description']) && (
              <div>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '8px',
                  color: '#333',
                  fontWeight: '600',
                  fontSize: '14px',
                }}>
                  <span>Office Instructions</span>
                  <button
                    type="button"
                    onClick={() => setShowInstructionsModal(true)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 10px',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: '#667eea',
                      background: '#f0f0ff',
                      border: '1px solid #667eea',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#667eea';
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f0f0ff';
                      e.currentTarget.style.color = '#667eea';
                    }}
                  >
                    <span>🔍</span> View Full
                  </button>
                </label>
                <div style={{
                  background: 'white',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '12px 14px',
                  fontSize: '14px',
                  color: '#555',
                  lineHeight: '1.6',
                  maxHeight: '150px',
                  overflowY: 'auto',
                  whiteSpace: 'pre-wrap',
                  boxShadow: 'inset 0 -15px 15px -15px rgba(0,0,0,0.08)',
                }}>
                  {jobData["Hudson's Notes"] || jobData['Issue Description']}
                </div>
              </div>
            )}
          </div>
        )}

        {success && !showSuccessModal && (
          <div style={styles.success}>
            ✅ Work order submitted successfully!
          </div>
        )}

        {error && (
          <div style={styles.error}>
            ❌ {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Location & Schedule Section */}
          <div style={styles.sectionContainer}>
            <div style={styles.sectionTitle}>
              <span>📍</span> Location & Schedule
            </div>

            {/* Unit Number & Service Date Row */}
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Unit #</label>
                <input
                  type="number"
                  name="unitNumber"
                  value={formData.unitNumber}
                  onChange={handleChange}
                  placeholder="e.g., 729"
                  style={styles.input}
                  onFocus={(e) => { e.target.style.borderColor = '#667eea'; e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.15)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Service Date <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="date"
                  name="serviceDate"
                  value={formData.serviceDate}
                  onChange={handleChange}
                  required
                  style={styles.input}
                  onFocus={(e) => { e.target.style.borderColor = '#667eea'; e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.15)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
            </div>

            {/* Start Time & End Time Row */}
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Start Time <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="time"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleChange}
                  required
                  style={styles.input}
                  onFocus={(e) => { e.target.style.borderColor = '#667eea'; e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.15)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  End Time <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="time"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleChange}
                  required
                  style={styles.input}
                  onFocus={(e) => { e.target.style.borderColor = '#667eea'; e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.15)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
            </div>
            
            {/* Total Time Display */}
            {formData.startTime && formData.endTime && (
              <div style={{
                background: '#eeeeee',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                padding: '10px 14px',
                marginBottom: '0',
                fontSize: '14px',
                color: '#555',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <span>⏱️</span>
                <span>
                  <strong>Total Time:</strong>{' '}
                  {(() => {
                    const [startH, startM] = formData.startTime.split(':').map(Number);
                    const [endH, endM] = formData.endTime.split(':').map(Number);
                    const startMinutes = startH * 60 + startM;
                    const endMinutes = endH * 60 + endM;
                    let diffMinutes = endMinutes - startMinutes;
                    
                    if (diffMinutes < 0) {
                      return 'End time must be after start time';
                    }
                    
                    const hours = Math.floor(diffMinutes / 60);
                    const minutes = diffMinutes % 60;
                    
                    if (hours === 0) {
                      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
                    } else if (minutes === 0) {
                      return `${hours} hour${hours !== 1 ? 's' : ''}`;
                    } else {
                      return `${hours} hour${hours !== 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`;
                    }
                  })()}
                </span>
              </div>
            )}
          </div>

          {/* Work Details Section */}
          <div style={styles.sectionContainer}>
            <div style={styles.sectionTitle}>
              <span>📋</span> Work Details
            </div>

            {/* Worker Multi-Select */}
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Worker(s) <span style={{ color: 'red' }}>*</span>
              </label>
              <p style={styles.description}>Select one or more workers</p>
              <div 
                ref={workerDropdownRef}
                style={{
                  position: 'relative',
                  width: '100%',
                }}
              >
                <div
                  onClick={() => setWorkerDropdownOpen(!workerDropdownOpen)}
                  style={{
                    width: '100%',
                    minHeight: '50px',
                    padding: '8px 40px 8px 8px',
                    border: workerDropdownOpen ? '2px solid #667eea' : '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    transition: 'border-color 0.3s, box-shadow 0.3s',
                    background: 'white',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '6px',
                    alignItems: 'center',
                    cursor: 'pointer',
                    boxShadow: workerDropdownOpen ? '0 0 0 3px rgba(102, 126, 234, 0.15)' : 'none',
                    boxSizing: 'border-box',
                  }}
                >
                  {selectedWorkers.length === 0 && (
                    <span style={{ color: '#999', padding: '6px' }}>Click to select workers...</span>
                  )}
                  {selectedWorkers.map(worker => {
                    const colors = WORKER_COLORS[worker] || WORKER_COLORS['Other'];
                    return (
                      <span
                        key={worker}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          background: colors.bg,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '6px',
                          padding: '4px 8px',
                          fontSize: '13px',
                          color: colors.text,
                          fontWeight: '500',
                        }}
                      >
                        {worker}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedWorkers(prev => prev.filter(w => w !== worker));
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: colors.text,
                            cursor: 'pointer',
                            padding: '0',
                            fontSize: '14px',
                            lineHeight: '1',
                            opacity: '0.7',
                          }}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
                <span style={{
                  position: 'absolute',
                  right: '14px',
                  top: '50%',
                  transform: workerDropdownOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: '#666',
                  transition: 'transform 0.2s',
                  fontSize: '12px',
                }}>
                  ▼
                </span>
                {workerDropdownOpen && (
                  <div style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    maxHeight: '320px',
                    overflowY: 'hidden',
                    zIndex: 1000,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    display: 'flex',
                    flexDirection: 'column',
                  }}>
                    {/* Search Input */}
                    <div style={{
                      padding: '10px 12px',
                      borderBottom: '1px solid #e0e0e0',
                      background: '#fafafa',
                    }}>
                      <input
                        ref={workerSearchInputRef}
                        type="text"
                        value={workerSearch}
                        onChange={(e) => setWorkerSearch(e.target.value)}
                        placeholder="🔍 Search workers..."
                        autoFocus
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '14px',
                          outline: 'none',
                          boxSizing: 'border-box',
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#667eea'}
                        onBlur={(e) => e.target.style.borderColor = '#ddd'}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    {/* Worker List */}
                    <div style={{
                      overflowY: 'auto',
                      maxHeight: '260px',
                    }}>
                      {filteredWorkers.length === 0 ? (
                        <div style={{
                          padding: '16px',
                          textAlign: 'center',
                          color: '#999',
                          fontSize: '14px',
                        }}>
                          No workers found
                        </div>
                      ) : (
                        filteredWorkers.map((worker, idx) => {
                          const isSelected = selectedWorkers.includes(worker);
                          const colors = WORKER_COLORS[worker] || WORKER_COLORS['Other'];
                          return (
                            <div
                              key={worker}
                              onClick={() => {
                                if (isSelected) {
                                  setSelectedWorkers(prev => prev.filter(w => w !== worker));
                                } else {
                                  setSelectedWorkers(prev => [...prev, worker]);
                                }
                              }}
                              style={{
                                padding: '12px 16px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: '#333',
                                borderBottom: idx < filteredWorkers.length - 1 ? '1px solid #f0f0f0' : 'none',
                                transition: 'background-color 0.15s',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                background: isSelected ? `linear-gradient(135deg, ${colors.bg} 0%, ${colors.bg} 100%)` : 'white',
                              }}
                              onMouseEnter={(e) => {
                                if (!isSelected) e.currentTarget.style.background = '#f8f9fa';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = isSelected ? colors.bg : 'white';
                              }}
                            >
                              <div style={{
                                width: '20px',
                                height: '20px',
                                border: isSelected ? `2px solid ${colors.border}` : '2px solid #ccc',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                transition: 'all 0.15s',
                                background: isSelected ? colors.border : 'white',
                              }}>
                                {isSelected && (
                                  <span style={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>✓</span>
                                )}
                              </div>
                              <div style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                background: `linear-gradient(135deg, ${colors.border} 0%, ${colors.text} 100%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '14px',
                                fontWeight: '600',
                                flexShrink: 0,
                              }}>
                                {worker.charAt(0)}
                              </div>
                              <span style={{ flex: 1 }}>{worker}</span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Description of Work Done */}
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Description of Work Done <span style={{ color: 'red' }}>*</span>
              </label>
              <textarea
                name="descriptionOfWorkDone"
                value={formData.descriptionOfWorkDone}
                onChange={handleChange}
                placeholder="Describe the work completed..."
                required
                style={styles.textarea}
                onFocus={(e) => { e.target.style.borderColor = '#667eea'; e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.15)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = 'none'; }}
              />
            </div>

            {/* Parts Used */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Parts Used From Stock</label>
              <p style={styles.description}>Please include each material used, and number of copies and exact model numbers. (Include Quantity, Model Number, BE SPECIFIC).</p>
              <textarea
                name="partsUsedFromStock"
                value={formData.partsUsedFromStock}
                onChange={handleChange}
                placeholder="e.g., 2x Filter (Model ABC-123), 1x Thermostat (Honeywell T6), or 'Nothing'"
                style={{ ...styles.textarea, minHeight: '80px' }}
                onFocus={(e) => { e.target.style.borderColor = '#667eea'; e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.15)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = 'none'; }}
              />
            </div>

            {/* Next Steps For Office */}
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Next Steps For Office <span style={{ color: 'red' }}>*</span>
              </label>
              <p style={styles.description}>What should the back office do? Please include info and details in the next question.</p>
              <div 
                ref={nextStepsDropdownRef}
                style={{
                  position: 'relative',
                  width: '100%',
                }}
              >
                <div
                  onClick={() => setNextStepsDropdownOpen(!nextStepsDropdownOpen)}
                  style={{
                    width: '100%',
                    minHeight: '50px',
                    padding: '14px 40px 14px 16px',
                    border: nextStepsDropdownOpen ? '2px solid #667eea' : '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontFamily: 'inherit',
                    transition: 'border-color 0.3s, box-shadow 0.3s',
                    background: 'white',
                    cursor: 'pointer',
                    boxShadow: nextStepsDropdownOpen ? '0 0 0 3px rgba(102, 126, 234, 0.15)' : 'none',
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {formData.nextStepsForOffice ? (
                    <span style={{ 
                      color: '#333',
                      lineHeight: '1.4',
                    }}>
                      {formData.nextStepsForOffice}
                    </span>
                  ) : (
                    <span style={{ color: '#999' }}>Select an option...</span>
                  )}
                </div>
                <span style={{
                  position: 'absolute',
                  right: '14px',
                  top: '50%',
                  transform: nextStepsDropdownOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: '#666',
                  transition: 'transform 0.2s',
                  fontSize: '12px',
                }}>
                  ▼
                </span>
                {nextStepsDropdownOpen && (
                  <div style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    maxHeight: '320px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  }}>
                    {NEXT_STEPS_OFFICE_OPTIONS.map((option, idx) => {
                      const isSelected = formData.nextStepsForOffice === option;
                      const isGreen = option.startsWith('✅');
                      const bgColor = isGreen ? '#e8f5e9' : '#ffebee';
                      const borderColor = isGreen ? '#4caf50' : '#f44336';
                      return (
                        <div
                          key={option}
                          onClick={() => {
                            setFormData(prev => ({ ...prev, nextStepsForOffice: option }));
                            setNextStepsDropdownOpen(false);
                          }}
                          style={{
                            padding: '14px 16px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: '#333',
                            borderBottom: idx < NEXT_STEPS_OFFICE_OPTIONS.length - 1 ? '1px solid #f0f0f0' : 'none',
                            transition: 'background-color 0.15s',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '12px',
                            background: isSelected ? bgColor : 'white',
                            borderLeft: isSelected ? `3px solid ${borderColor}` : '3px solid transparent',
                            lineHeight: '1.4',
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) e.currentTarget.style.background = '#f8f9fa';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = isSelected ? bgColor : 'white';
                          }}
                        >
                          <span style={{ flex: 1, lineHeight: '1.5' }}>
                            {option}
                          </span>
                          {isSelected && (
                            <span style={{ 
                              color: borderColor, 
                              fontWeight: 'bold',
                              flexShrink: 0,
                            }}>✓</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Next Steps */}
            <div style={{ ...styles.formGroup, marginBottom: 0 }}>
              <label style={styles.label}>
                Next Steps & Recommendations <span style={{ color: 'red' }}>*</span>
              </label>
              <p style={styles.description}>What are the recommendations? Is there more work required, and what would be the cost? Any other notes?</p>
              <textarea
                name="nextSteps"
                value={formData.nextSteps}
                onChange={handleChange}
                placeholder="e.g., Unit needs compressor replacement (~$800). Recommend scheduling follow-up visit..."
                required
                style={{ ...styles.textarea, minHeight: '80px' }}
                onFocus={(e) => { e.target.style.borderColor = '#667eea'; e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.15)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = 'none'; }}
              />
            </div>
          </div>

          {/* Photos Section */}
          <div style={styles.sectionContainer}>
            <div style={styles.sectionTitle}>
              <span>📸</span> Photos
            </div>

            <FileUpload
              label="Photos/Videos of Work (BEFORE)"
              value={photosBeforeUrls}
              onChange={setPhotosBeforeUrls}
              onUploadStatusChange={(uploading) => setIsUploadingPhotos(prev => uploading || photosAfterUrls.length > 0 || receiptPhotoUrls.length > 0)}
              multiple={true}
              allowVideo={true}
            />

            <FileUpload
              label="Photos/Videos of Work (AFTER)"
              value={photosAfterUrls}
              onChange={setPhotosAfterUrls}
              onUploadStatusChange={(uploading) => setIsUploadingPhotos(prev => uploading || photosBeforeUrls.length > 0 || receiptPhotoUrls.length > 0)}
              multiple={true}
              allowVideo={true}
            />

            <FileUpload
              label="Receipt Photos"
              value={receiptPhotoUrls}
              onChange={setReceiptPhotoUrls}
              onUploadStatusChange={(uploading) => setIsUploadingPhotos(prev => uploading)}
              multiple={true}
              allowVideo={false}
            />
          </div>

          <button
            type="submit"
            style={{
              ...styles.submitBtn,
              opacity: loading ? 0.6 : 1,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
            disabled={loading}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 10px 20px rgba(102, 126, 234, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
          >
            {loading ? 'Submitting...' : 'Submit Work Order'}
          </button>
        </form>
      </div>

      {/* Office Instructions Modal */}
      {showInstructionsModal && jobData && (jobData["Hudson's Notes"] || jobData['Issue Description']) && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px',
          }}
          onClick={() => setShowInstructionsModal(false)}
        >
          <div 
            style={{
              background: 'white',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '600px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 20px',
              borderBottom: '1px solid #e5e7eb',
              flexShrink: 0,
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '600',
                color: '#333',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <span>📋</span> Office Instructions
              </h3>
              <button
                type="button"
                onClick={() => setShowInstructionsModal(false)}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  border: 'none',
                  background: '#f0f0f0',
                  color: '#666',
                  fontSize: '18px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e0e0e0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f0f0f0';
                }}
              >
                ×
              </button>
            </div>
            {/* Modal Content */}
            <div style={{
              padding: '20px',
              overflowY: 'auto',
              flex: 1,
              fontSize: '15px',
              lineHeight: '1.7',
              color: '#333',
              whiteSpace: 'pre-wrap',
            }}>
              {jobData["Hudson's Notes"] || jobData['Issue Description']}
            </div>
          </div>
        </div>
      )}

      {/* Success Modal with Email Option */}
      {showSuccessModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '16px',
          }}
          onClick={() => setShowSuccessModal(false)}
        >
          <div 
            style={{
              background: 'white',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '400px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
              padding: '24px 20px',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
              <h2 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '600',
                color: 'white',
              }}>
                Work Order Submitted!
              </h2>
              <p style={{
                margin: '8px 0 0 0',
                fontSize: '14px',
                color: 'rgba(255, 255, 255, 0.9)',
              }}>
                Your work order has been saved successfully.
              </p>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '20px' }}>
              {!emailSent ? (
                <>
                  <p style={{
                    fontSize: '16px',
                    color: '#444',
                    marginBottom: '16px',
                    textAlign: 'center',
                    fontWeight: '500',
                  }}>
                    Would you like to receive a copy of this work order?
                  </p>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: '#333',
                      marginBottom: '6px',
                    }}>
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={emailForCopy}
                      onChange={(e) => setEmailForCopy(e.target.value)}
                      placeholder="Enter your email"
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        fontSize: '15px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        outline: 'none',
                        boxSizing: 'border-box',
                        transition: 'border-color 0.2s',
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#4CAF50'}
                      onBlur={(e) => e.target.style.borderColor = '#ddd'}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={async () => {
                      if (!emailForCopy || !emailForCopy.includes('@')) {
                        alert('Please enter a valid email address');
                        return;
                      }
                      setSendingEmail(true);
                      try {
                        const response = await fetch(`${API_BASE_URL}/api/work-orders/send-copy`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            email: emailForCopy,
                            workOrderPageId: submittedPageId,
                            jobId: jobId,  // Short job ID from URL query param (e.g., "NE-1626")
                          }),
                        });
                        const result = await response.json();
                        if (result.success) {
                          setEmailSent(true);
                        } else {
                          alert(result.error || 'Failed to send email. Please try again.');
                        }
                      } catch (err) {
                        console.error('Error sending email:', err);
                        alert('Failed to send email. Please try again.');
                      } finally {
                        setSendingEmail(false);
                      }
                    }}
                    disabled={sendingEmail || !emailForCopy}
                    style={{
                      width: '100%',
                      padding: '14px',
                      fontSize: '15px',
                      fontWeight: '600',
                      color: 'white',
                      background: sendingEmail || !emailForCopy ? '#ccc' : '#4CAF50',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: sendingEmail || !emailForCopy ? 'not-allowed' : 'pointer',
                      marginBottom: '10px',
                      transition: 'background-color 0.2s',
                    }}
                  >
                    {sendingEmail ? (
                      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                        <span style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid rgba(255,255,255,0.3)',
                          borderTopColor: 'white',
                          borderRadius: '50%',
                          animation: 'spin 0.8s linear infinite',
                        }} />
                        Sending...
                      </span>
                    ) : '📧 Send Me a Copy'}
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowSuccessModal(false)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#666',
                      background: 'transparent',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'transparent';
                    }}
                  >
                    No Thanks
                  </button>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '10px 0' }}>
                  <div style={{ fontSize: '32px', marginBottom: '12px' }}>📬</div>
                  <p style={{
                    fontSize: '15px',
                    color: '#333',
                    fontWeight: '500',
                    marginBottom: '8px',
                  }}>
                    Email sent successfully!
                  </p>
                  <p style={{
                    fontSize: '13px',
                    color: '#666',
                    marginBottom: '20px',
                  }}>
                    A copy has been sent to {emailForCopy}
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowSuccessModal(false)}
                    style={{
                      padding: '12px 32px',
                      fontSize: '15px',
                      fontWeight: '600',
                      color: 'white',
                      background: '#4CAF50',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                    }}
                  >
                    Done
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WorkOrder;
